import React from 'react';
import { motion } from 'framer-motion';
import { Clock, MapPin, ArrowRight } from 'lucide-react';
import WhatsAppButton from '../components/WhatsAppButton';

const Home = () => {
  const handleMapClick = () => {
    window.open('https://maps.app.goo.gl/YpjoQtwyg6RsaNjs7', '_blank');
  };

  const handleWhatsAppOrder = () => {
    window.open('https://wa.me/919749861665', '_blank');
  };

  const galleryImages = [
  {
    url: '/images/hero1.webp',
    alt: 'Modern pharmacy interior with bright shelves'
  },
  {
    url: '/images/gallery1.webp',
    alt: 'Assorted medicines and pills'
  },
  {
    url: '/images/gallery2.webp',
    alt: 'Amber medicine bottles'
  },
  {
    url: '/images/gallery3.webp',
    alt: 'Medical supplies on pharmacy shelves'
  }
];

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2
      }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.6 }
    }
  };

  return (
    <div className="min-h-screen bg-white" data-testid="home-page">
      {/* Hero Section */}
      <section className="pt-32 pb-20 md:pt-40 md:pb-32 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-white via-medical-mint/20 to-white">
        <div className="max-w-7xl mx-auto">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            {/* Left Content */}
            <motion.div
              initial="hidden"
              animate="visible"
              variants={containerVariants}
              className="space-y-8"
            >
              <motion.div variants={itemVariants} className="space-y-4">
                <h1
                  data-testid="hero-heading"
                  className="text-5xl md:text-6xl lg:text-7xl font-playfair font-bold text-medical-green leading-tight"
                >
                  Anubhav Medicals
                </h1>
                <p
                  data-testid="hero-subtext"
                  className="text-xl md:text-2xl font-manrope text-gray-600"
                >
                  Your Trusted Neighborhood Medical Store
                </p>
                <p className="text-base md:text-lg font-manrope text-gray-500">
                  Serving the NITK Surathkal community with genuine medicines and care.
                </p>
              </motion.div>

              <motion.div variants={itemVariants} className="flex items-center space-x-4 text-medical-green">
                <Clock className="w-6 h-6" data-testid="clock-icon" />
                <span className="text-lg font-manrope font-medium" data-testid="timings-text">
                  Open from 9:00 AM to 6:00 PM
                </span>
              </motion.div>

              <motion.button
                variants={itemVariants}
                onClick={handleWhatsAppOrder}
                data-testid="order-whatsapp-button"
                className="bg-medical-green text-white hover:bg-medical-green-dark rounded-full px-8 py-4 font-manrope font-medium text-lg transition-all duration-300 shadow-lg hover:shadow-xl hover:-translate-y-0.5 flex items-center space-x-2 group"
              >
                <span>Order via WhatsApp</span>
                <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform duration-300" />
              </motion.button>
            </motion.div>

            {/* Right Image */}
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.8 }}
              className="relative"
            >
              <div
                data-testid="hero-image"
                className="relative rounded-[30%_70%_70%_30%/30%_30%_70%_70%] overflow-hidden shadow-2xl"
                style={{ aspectRatio: '4/5' }}
              >
                
                <img
  src="/images/hero1.webp"
  alt="Modern pharmacy interior"
  loading="eager"
  fetchpriority="high"
  decoding="async"
  className="w-full h-full object-cover"
/>
              </div>
              {/* Decorative elements */}
              <div className="absolute -top-4 -right-4 w-24 h-24 bg-medical-gold/20 rounded-full blur-2xl -z-10" />
              <div className="absolute -bottom-4 -left-4 w-32 h-32 bg-medical-mint rounded-full blur-3xl -z-10" />
            </motion.div>
          </div>
        </div>
      </section>

      {/* Photo Gallery Section */}
      <section className="py-20 md:py-32 px-4 sm:px-6 lg:px-8 bg-white">
        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          variants={containerVariants}
          className="max-w-7xl mx-auto"
        >
          <motion.div variants={itemVariants} className="text-center mb-16">
            <h2
              data-testid="gallery-heading"
              className="text-4xl md:text-5xl font-playfair font-bold text-medical-green mb-4"
            >
              Our Store
            </h2>
            <p className="text-lg text-gray-600 font-manrope">
              A glimpse into our well-stocked pharmacy
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6" data-testid="photo-gallery">
            {galleryImages.map((image, index) => (
              <motion.div
                key={index}
                variants={itemVariants}
                data-testid={`gallery-image-${index}`}
                className="relative group overflow-hidden rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-300"
                style={{ aspectRatio: '3/4' }}
              >
             <img
  src={image.url}
  alt={image.alt}
  loading="lazy"
  decoding="async"
  className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
/>
                <div className="absolute inset-0 bg-gradient-to-t from-medical-green/60 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
              </motion.div>
            ))}
          </div>
        </motion.div>
      </section>

      {/* Address & Location Section */}
      <section className="py-20 md:py-32 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-medical-mint/30 to-white">
        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          variants={containerVariants}
          className="max-w-4xl mx-auto text-center space-y-8"
        >
          <motion.div variants={itemVariants}>
            <h2
              data-testid="location-heading"
              className="text-4xl md:text-5xl font-playfair font-bold text-medical-green mb-6"
            >
              Visit Us
            </h2>
            <p
              data-testid="address-text"
              className="text-xl md:text-2xl font-manrope text-gray-700 mb-8"
            >
              Aravali Hostel, NITK, Surathkal, Mangalore
            </p>
          </motion.div>

          <motion.button
            variants={itemVariants}
            onClick={handleMapClick}
            data-testid="map-button"
            className="bg-white border-2 border-medical-green text-medical-green hover:bg-medical-green hover:text-white rounded-full px-8 py-4 font-manrope font-medium text-lg transition-all duration-300 shadow-lg hover:shadow-xl inline-flex items-center space-x-3 group"
          >
            <MapPin className="w-5 h-5" />
            <span>View on Google Maps</span>
            <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform duration-300" />
          </motion.button>
        </motion.div>
      </section>

      <WhatsAppButton />
    </div>
  );
};

export default Home;
